import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RotateCcw, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { prixiaService } from '../services/prixia-service';
import type { TiendasDeLaEmpresa } from '../types/prixia.types';
import { horasEnTexto } from '@/features/dynamic-pricing/lib/lecturas-de-prixia';

interface Props {
  tenantId: string;
  /** Una empresa archivada no se toca: restaurala para cambiarle las tiendas. */
  soloLectura?: boolean;
}

function resumenEnTexto(r: TiendasDeLaEmpresa['resumen']): string | undefined {
  if (!r) return undefined;
  const partes: string[] = [];
  if (r.creadas.length + r.habilitadas.length > 0) {
    partes.push(`Habilitadas: ${[...r.creadas, ...r.habilitadas].join(', ')}.`);
  }
  if (r.quitadas.length > 0) partes.push(`Quitadas: ${r.quitadas.join(', ')}.`);
  return partes.length > 0 ? partes.join(' ') : 'Las tiendas quedaron como estaban.';
}

/**
 * «Tiendas disponibles» de una empresa (spec 288 C2; plan 297): la tercera capa de su
 * contrato, junto a sus módulos y sus permisos. Habilitar le da su copia de la tienda;
 * quitarla la apaga sin borrar nada. Acá también se enciende su permiso «Monitoreo de
 * lecturas» (C9) y se fija su cupo de «leer ahora» (C8).
 */
export function TenantTiendasPrixiaPanel({ tenantId, soloLectura = false }: Props) {
  const { toast } = useToast();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [datos, setDatos] = useState<TiendasDeLaEmpresa | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [marcadas, setMarcadas] = useState<Set<string>>(new Set());
  const [monitoreo, setMonitoreo] = useState(false);
  const [cupo, setCupo] = useState('');
  // Lo que no es un número (el navegador lo deja vacío) es inválido, no «vacío = el de la
  // plataforma».
  const [cupoIlegible, setCupoIlegible] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const aplicarDatos = useCallback((d: TiendasDeLaEmpresa) => {
    setDatos(d);
    setMarcadas(new Set(d.tiendas.filter((t) => t.habilitada).map((t) => t.storeId)));
    setMonitoreo(d.acceso.readingMonitor);
    setCupo(d.acceso.readNowDailyQuota == null ? '' : String(d.acceso.readNowDailyQuota));
    setCupoIlegible(false);
  }, []);

  const cargar = useCallback(async () => {
    setError(null);
    try {
      aplicarDatos(await prixiaService.tiendasDeLaEmpresa(tenantId));
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudieron leer las tiendas de esta empresa.'));
    }
  }, [tenantId, aplicarDatos]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const cupoNumero = cupo.trim() === '' ? null : Number(cupo);
  const cupoValido =
    !cupoIlegible &&
    (cupoNumero === null || (Number.isInteger(cupoNumero) && cupoNumero >= 0 && cupoNumero <= 100));

  const quitadas = useMemo(
    () => (datos ? datos.tiendas.filter((t) => t.habilitada && !marcadas.has(t.storeId)) : []),
    [datos, marcadas]
  );

  const cambiaronTiendas =
    datos !== null && datos.tiendas.some((t) => t.habilitada !== marcadas.has(t.storeId));
  const cambioMonitoreo = datos !== null && monitoreo !== datos.acceso.readingMonitor;
  const cambioCupo = datos !== null && cupoNumero !== datos.acceso.readNowDailyQuota;
  const huboCambios = cambiaronTiendas || cambioMonitoreo || cambioCupo;

  const alternar = (storeId: string) =>
    setMarcadas((prev) => {
      const siguiente = new Set(prev);
      if (siguiente.has(storeId)) siguiente.delete(storeId);
      else siguiente.add(storeId);
      return siguiente;
    });

  const aplicar = async () => {
    setGuardando(true);
    try {
      // Sólo lo que cambió: guardar el cupo no vuelve a escribir las tiendas (ni pisa lo
      // que otro administrador cambió mientras tanto).
      const cambio: {
        storeIds?: string[];
        readingMonitor?: boolean;
        readNowDailyQuota?: number | null;
      } = {};
      if (cambiaronTiendas) cambio.storeIds = [...marcadas];
      if (cambioMonitoreo) cambio.readingMonitor = monitoreo;
      if (cambioCupo) cambio.readNowDailyQuota = cupoNumero;
      const r = await prixiaService.guardarEmpresa(tenantId, cambio);
      aplicarDatos(r);
      toast({ title: 'Tiendas guardadas', description: resumenEnTexto(r.resumen) });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'No se pudo guardar',
        description: mensajeDeError(err, 'Intentá de nuevo.'),
      });
      // 404/409: lo que se ve ya no es lo que hay (otra persona cambió algo, o la
      // empresa/tienda ya no está): se vuelve a leer.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 404 || status === 409) void cargar();
    } finally {
      setGuardando(false);
    }
  };

  const guardar = () => {
    if (!datos) return;
    if (quitadas.length === 0) {
      void aplicar();
      return;
    }
    const nombres = quitadas.map((t) => t.name).join(', ');
    const una = quitadas.length === 1;
    confirm({
      title: `¿Quitarle ${una ? 'una tienda' : `${quitadas.length} tiendas`} a ${datos.empresa.name}?`,
      description: `${nombres} deja${una ? '' : 'n'} de recibir lecturas y sale${una ? '' : 'n'} de las pantallas y los análisis de la empresa. No se borra nada: sus vínculos y su historial quedan, y vuelven si se la habilita de nuevo.`,
      confirmText: 'Sí, quitar',
      variant: 'destructive',
      onConfirm: aplicar,
    });
  };

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
        <p className="text-sm font-medium text-destructive">{error}</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => void cargar()}>
          Reintentar
        </Button>
      </div>
    );
  }

  if (!datos) {
    return (
      <div className="space-y-3" aria-busy="true">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <ConfirmDialog />

      {datos.tiendas.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Todavía no hay tiendas en el catálogo de Prixia. Se agregan desde «Tiendas de Prixia».
        </p>
      ) : (
        <div className="grid gap-2 rounded-lg border p-3 sm:grid-cols-2">
          {datos.tiendas.map((t) => {
            const bloqueada =
              !t.habilitada && t.copias.every((c) => c.fusionada) && t.copiaSinMigrar !== null;
            return (
              <div
                key={t.storeId}
                className="flex items-start gap-2 rounded p-1.5 hover:bg-muted/50"
              >
                <Checkbox
                  className="mt-0.5"
                  checked={marcadas.has(t.storeId)}
                  disabled={bloqueada}
                  onCheckedChange={() => alternar(t.storeId)}
                  aria-label={`Habilitar ${t.name}`}
                />
                <div className="flex-1 text-sm">
                  <span className="font-medium">{t.name}</span>{' '}
                  <span className="text-xs text-muted-foreground">{t.url}</span>
                  {!t.tiendaActiva && (
                    <Badge variant="outline" className="ml-2 text-[10px]">
                      Apagada en Prixia
                    </Badge>
                  )}
                  {t.repetida && (
                    <span className="block text-xs text-amber-600">
                      Tiene dos copias de esta tienda.{' '}
                      <Link className="underline" to={`/plataforma/tiendas/${t.storeId}`}>
                        Fusionalas desde la ficha de la tienda
                      </Link>
                      .
                    </span>
                  )}
                  {bloqueada && (
                    <span className="block text-xs text-muted-foreground">
                      {`Ya tiene su propia copia («${t.copiaSinMigrar?.name ?? ''}») sin pasar al catálogo de Prixia: se habilita con la migración.`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="grid gap-4 rounded-lg border p-3 md:grid-cols-2">
        <div className="flex items-start gap-3">
          <Switch
            checked={monitoreo}
            onCheckedChange={setMonitoreo}
            aria-label="Monitoreo de lecturas"
            className="mt-0.5"
          />
          <div className="text-sm">
            <p className="font-medium">Monitoreo de lecturas</p>
            <p className="text-xs text-muted-foreground">
              {`Sus administradores ven las lecturas de sus tiendas (con sus errores) y pueden cambiar cada cuánto se leen, dentro de los mínimos de la plataforma${
                datos.topesDeLaEmpresa
                  ? ` y como mucho cada ${horasEnTexto(datos.topesDeLaEmpresa.maximoChequeoHoras)} el chequeo y cada ${horasEnTexto(datos.topesDeLaEmpresa.maximoBarridoHoras)} el barrido`
                  : ''
              }. El cambio rige para todas las empresas que tienen la tienda y queda registrado.`}
            </p>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`cupo-${tenantId}`}>Lecturas a pedido por día</Label>
          <Input
            id={`cupo-${tenantId}`}
            type="number"
            min={0}
            max={100}
            className="w-32"
            value={cupo}
            placeholder={String(datos.cupoDeLaPlataforma)}
            onChange={(e) => {
              setCupoIlegible(e.target.validity.badInput);
              setCupo(e.target.value);
            }}
          />
          <p className="text-xs text-muted-foreground">
            {`Vacío = las de la plataforma (${datos.cupoDeLaPlataforma}). 0 = esta empresa no puede pedir lecturas nuevas.`}
          </p>
          {!cupoValido && (
            <p className="text-xs text-destructive">
              Tiene que ser un número entero entre 0 y 100.
            </p>
          )}
        </div>
      </div>

      {soloLectura ? (
        <p className="border-t pt-4 text-xs text-muted-foreground">
          Está archivada: restaurala para cambiarle las tiendas.
        </p>
      ) : (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
          <Button
            variant="outline"
            onClick={() => aplicarDatos(datos)}
            disabled={!huboCambios || guardando}
          >
            <RotateCcw className="mr-1.5 h-4 w-4" aria-hidden="true" />
            Descartar cambios
          </Button>
          <Button onClick={guardar} disabled={!huboCambios || guardando || !cupoValido}>
            {guardando ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Save className="mr-1.5 h-4 w-4" aria-hidden="true" />
            )}
            Guardar tiendas
          </Button>
        </div>
      )}
    </div>
  );
}

export default TenantTiendasPrixiaPanel;
